import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';

export const MESSAGE_GOALS = [
  'Заказ/бронирование',
  'Сотрудничество',
  'Вопрос',
  'Коммерческое предложение',
  'Другое'
];

export function useInboxMessages({ read } = {}) {
  return useQuery(
    ['messages', 'inbox', { read }],
    async () => {
      const res = await api.get('/api/messages/inbox', {
        params: typeof read === 'boolean' ? { read } : undefined
      });
      return res?.data;
    },
    {
      // avoid hammering
      staleTime: 10_000
    }
  );
}

export function useInboxMessagesPage({ read, goal, q, page, perPage = 10 } = {}) {
  return useQuery(
    ['messages', 'inbox', 'page', { read, goal, q, page, perPage }],
    async () => {
      const params = {
        page: page || 1,
        perPage
      };
      if (typeof read === 'boolean') params.read = read;
      if (goal) params.goal = goal;
      if (q) params.q = q;

      const res = await api.get('/api/messages/inbox', { params });
      return res?.data;
    },
    {
      keepPreviousData: true,
      staleTime: 10_000
    }
  );
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation(
    async ({ toUserId, goal, body }) => {
      const res = await api.post('/api/messages', { toUserId, goal, body });
      return res?.data;
    },
    {
      onSuccess: () => {
        // refresh inbox
        qc.invalidateQueries(['messages', 'inbox']);
      }
    }
  );
}

export function useMarkMessageRead() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id }) => {
      const res = await api.post(`/api/messages/${encodeURIComponent(id)}/mark-read`, {});
      return res?.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['messages', 'inbox']);
      }
    }
  );
}

// =============================
// Threads API (new)
// =============================

export function useMessageThreadsPage({ q, page, perPage = 10 } = {}) {
  return useQuery(
    ['messages', 'threads', 'page', { q, page, perPage }],
    async () => {
      const params = { page: page || 1, perPage };
      if (q) params.q = q;
      const res = await api.get('/api/messages/threads', { params });
      return res?.data;
    },
    {
      keepPreviousData: true,
      staleTime: 10_000
    }
  );
}

export function useMessageThreadDetail({ threadId, page, perPage = 10 } = {}) {
  return useQuery(
    ['messages', 'threads', 'detail', { threadId, page, perPage }],
    async () => {
      if (!threadId) return null;
      const params = { page: page || 1, perPage };
      const res = await api.get(`/api/messages/threads/${encodeURIComponent(threadId)}`, { params });
      return res?.data;
    },
    {
      enabled: !!threadId,
      keepPreviousData: true,
      staleTime: 5_000
    }
  );
}

export function useReplyToThread() {
  const qc = useQueryClient();
  return useMutation(
    async ({ threadId, goal, body }) => {
      const res = await api.post(`/api/messages/threads/${encodeURIComponent(threadId)}/reply`, { goal, body });
      return res?.data;
    },
    {
      onSuccess: (_data, vars) => {
        // refresh thread list + current thread detail
        qc.invalidateQueries(['messages', 'threads']);
        qc.invalidateQueries(['messages', 'threads', 'detail']);
        if (vars?.threadId) qc.invalidateQueries(['messages', 'threads', 'detail', { threadId: vars.threadId }]);
      }
    }
  );
}

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation(
    async ({ threadId }) => {
      const res = await api.post(`/api/messages/threads/${encodeURIComponent(threadId)}/mark-read`, {});
      return res?.data;
    },
    {
      onSuccess: (_data, vars) => {
        qc.invalidateQueries(['messages', 'threads']);
        qc.invalidateQueries(['messages', 'threads', 'detail']);
        if (vars?.threadId) qc.invalidateQueries(['messages', 'threads', 'detail', { threadId: vars.threadId }]);
      }
    }
  );
}

export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation(
    async ({ threadId }) => {
      const res = await api.delete(`/api/messages/threads/${encodeURIComponent(threadId)}`);
      return res?.data;
    },
    {
      onSuccess: (_data, vars) => {
        qc.invalidateQueries(['messages', 'threads']);
        qc.invalidateQueries(['messages', 'threads', 'detail']);
        if (vars?.threadId) qc.invalidateQueries(['messages', 'threads', 'detail', { threadId: vars.threadId }]);
      }
    }
  );
}
